import * as React from 'react';
import { Box, StackLayout, Button } from '@leagueplatform/genesis-core';
import { JsonFormsDispatch } from '@jsonforms/react';
import {
  composePaths,
  findUISchema,
  JsonFormsUISchemaRegistryEntry,
} from '@jsonforms/core';
import { AnnotatedJsonSchema } from '@web-config-app/core';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
  RelationshipValue,
  EntitySelectControlSchema,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';
import {
  EntityFormArrayControl,
  NumberIcon,
  ArrayActionButtons,
  CompoundPrimitiveGroupFieldset,
  useEntityReferenceArrayItemActions,
  useEntityRelationshipSchema,
} from '@web-config-app/entity-form';
import { ArrayEntityReferenceControlProvider } from './array-entity-reference-provider.component';

interface UseEntityReferenceItemsProps {
  schema: AnnotatedJsonSchema;
  data: string[] | undefined;
  relationshipValue: RelationshipValue[] | undefined;
  relationshipSchema: AnnotatedJsonSchema | undefined;
}

/**
 * If the reference is a string ID, we use `data`.
 * If the reference ONLY is a relationship, we use `relationshipValue`.
 * If the reference exists as both a string ID and a relationship, we use `data`.
 */

const getEntityReferenceItems = ({
  schema,
  data,
  relationshipValue,
}: UseEntityReferenceItemsProps) => {
  if (schema.type === 'string') {
    return data ?? [];
  }

  return Array.isArray(relationshipValue) ? relationshipValue : [];
};

/**
 * Renders for schema type 'array' where `schema.items` has a defined `x-entity-reference.relationship`
 */

export const ArrayEntityReferenceControlContents = ({
  data,
  schema,
  enabled,
  banner,
  hint,
  label,
  moveDown,
  moveUp,
  removeItems,
  addItem,
  arrayAddLabel,
  itemLabel,
  inputStatus,
  statusMessage,
  path,
  uischema,
  uischemas,
  renderers,
  cells,
  rootSchema,
  createSortableId,
  SortableItem,
  SortableWrapper,
  setRelationshipItemCount,
  registerAfterReorderCallback,
}: EntityFormArrayControlProps) => {
  const relationshipUtils = useEntityRelationshipSchema({
    schema: schema as EntitySelectControlSchema,
  });
  const {
    value,
    relationshipDataSchema,
    removeRelationshipItem,
    addRelationshipItem,
    moveRelationshipItemUp,
    moveRelationshipItemDown,
    reorderRelationshipItems,
  } = relationshipUtils;
  const items = getEntityReferenceItems({
    schema,
    data,
    relationshipValue: value as RelationshipValue[],
    relationshipSchema: relationshipDataSchema,
  });

  React.useEffect(() => {
    setRelationshipItemCount?.(items.length);
    return () => {
      setRelationshipItemCount?.(undefined);
    };
  }, [items.length, setRelationshipItemCount]);

  React.useEffect(() => {
    if (!relationshipDataSchema) {
      registerAfterReorderCallback(null);
      return () => {
        registerAfterReorderCallback(null);
      };
    }

    const handleReorder = (fromIndex: number, toIndex: number) => {
      reorderRelationshipItems?.(fromIndex, toIndex);
    };

    registerAfterReorderCallback(handleReorder);

    return () => {
      registerAfterReorderCallback(null);
    };
  }, [
    registerAfterReorderCallback,
    relationshipDataSchema,
    reorderRelationshipItems,
  ]);

  const { handleMoveUp, handleMoveDown, handleAddItem, handleRemoveItem } =
    useEntityReferenceArrayItemActions({
      schema,
      relationshipDataSchema,
      addItem,
      moveUp,
      moveDown,
      removeItems,
      addRelationshipItem,
      removeRelationshipItem,
      moveRelationshipItemUp,
      moveRelationshipItemDown,
    });

  const childUiSchema = React.useMemo(
    () =>
      findUISchema(
        uischemas as JsonFormsUISchemaRegistryEntry[],
        schema,
        uischema.scope,
        path,
        undefined,
        uischema,
        rootSchema,
      ),
    [uischemas, schema, path, uischema, rootSchema],
  );

  return (
    <CompoundPrimitiveGroupFieldset
      label={label}
      banner={banner}
      hint={hint}
      inputStatus={inputStatus}
      statusMessage={statusMessage}
    >
      <SortableWrapper>
        <StackLayout
          role="list"
          aria-label={label}
          css={{
            marginBlockStart: '$threeQuarters',
            paddingInlineStart: '$none',
            border: `${
              items.length > 0
                ? `$borderWidths$thin solid $onSurfaceBorderSubdued`
                : `none`
            }`,
            borderRadius: '$medium',
            listStyleType: 'none',
          }}
          horizontalAlignment="stretch"
        >
          <ArrayEntityReferenceControlProvider>
            {items.map((item: any, index: number) => {
              const listItemNumber = index + 1;
              const lastItem = items.length === listItemNumber;
              const childPath = composePaths(path, `${index}`);
              const sortableId = createSortableId(index);

              return (
                <SortableItem
                  key={sortableId}
                  id={sortableId}
                  disabled={enabled === false}
                  containerCss={{
                    width: '100%',
                    flexGrow: 1,
                    padding: '$half',
                    marginBlockStart: 0,
                    borderBottom: `${
                      lastItem
                        ? 'none'
                        : '$borderWidths$thin solid $onSurfaceBorderSubdued'
                    }`,
                    '&:first-of-type': {
                      borderRadius: '$medium $medium 0 0',
                    },
                    '&:last-of-type': {
                      borderRadius: '0 0 $medium $medium',
                    },
                    '&:first-of-type:last-of-type': {
                      borderRadius: '$medium',
                    },
                    backgroundColor: '$surfaceBackgroundPrimary',
                  }}
                >
                  {(
                    dragHandleProps: SortableItemRenderProps['dragHandleProps'],
                  ) => (
                    <StackLayout
                      role="listitem"
                      orientation="horizontal"
                      verticalAlignment="center"
                      horizontalAlignment="stretch"
                      css={{
                        width: '100%',
                      }}
                      aria-label={itemLabel}
                    >
                      <Box css={{ flexGrow: 0 }}>
                        <NumberIcon
                          number={listItemNumber}
                          css={{ margin: 'auto $half' }}
                        />
                      </Box>

                      <Box
                        as="fieldset"
                        css={{
                          paddingX: '$two',
                          borderWidth: '0',
                          flexGrow: 1,
                        }}
                      >
                        <JsonFormsDispatch
                          enabled={enabled}
                          schema={schema}
                          uischema={childUiSchema}
                          path={childPath}
                          key={childPath}
                          renderers={renderers}
                          cells={cells}
                        />
                      </Box>

                      <Box css={{ flexGrow: 0 }}>
                        <ArrayActionButtons
                          onDelete={() => handleRemoveItem(item, path, index)}
                          onMoveDown={() => handleMoveDown(item, path, index)}
                          onMoveUp={() => handleMoveUp(item, path, index)}
                          itemLabel={itemLabel as string}
                          itemIndex={index}
                          arrayLength={items.length}
                          disableAllActions={enabled === false}
                          dragHandleProps={dragHandleProps}
                        />
                      </Box>
                    </StackLayout>
                  )}
                </SortableItem>
              );
            })}
          </ArrayEntityReferenceControlProvider>
        </StackLayout>
      </SortableWrapper>

      <Button onClick={() => handleAddItem(path)}>{arrayAddLabel}</Button>
    </CompoundPrimitiveGroupFieldset>
  );
};

const renderArrayControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayEntityReferenceControlContents {...formContentsProps} />
);

export const ArrayEntityReferenceControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={renderArrayControl} />
);
